import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import BackButton from "components/common/BackButton";
import Snackbar from "components/common/Snackbar";
import AboveProperty from "components/features/AboveProperty";
import PlanProgress from "components/features/AboveProperty/PlanView/PlansProgress";
import CorpDashboard from "components/features/Corp/CorpDashboard";
import IndividualProperty from "components/features/IndividualProperty";

import AdminConsole from "components/features/AdminConsole";
import MyLists from "components/features/Corp/MyLists";
import Feedback from "components/features/Feedback";
import EditFeedback from "components/features/Feedback/EditFeedback";
import IndividualUser from "components/features/IndividualUser";
import ManageSuggestions from "components/features/ManageSuggestions";
import ReleaseDialog from "components/features/MyDashboard/ReleaseHighlight/ResourceDialog";
import MyPlanDetails from "components/features/MyPlanDetails";
import MyPlans from "components/features/MyPlans";
import Progress from "components/features/Progress";
import HotelUserDashboard from "components/features/dashboard/HotelUserDashboard";
import Library from "components/features/library";
import LibraryHallSearch from "components/features/library/Search";
import FunctionalArea from "components/features/library/functionalArea";
import PlanDetails from "components/features/planDetails";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { nestedTernaryCheckerCond1 } from "utils/CommonChecker";
import { abovePropUserView } from "utils/CommonConstants";
import { decrypt, getInitialWizardTaken, getIsCorpUser } from "utils/CommonFunctions";
import { recommendationAccessTypes } from "utils/Types";
import { routes } from "utils/routes";
import { theme as MUITheme } from "../../styles/Theme";
import EditPermission from "../features/AdminConsole/EditPermission";
import DialogsContextLayout from "./DialogsContextLayout";
import "./Main.scss";

const BoostRoutes = () => {
  const location = useLocation();
  const { isCorp, isConsultant, isAbovePropertyUserFetched, adminConsoleAccess, uploadRecommendationAccess } =
    useAppSelector((state) => state.app.loggedInUserDetails);
  const isAboveProperty = useAppSelector((state) => state.app.loggedInUserDetails.isAbovePropertyUser);
  const manageFeedbackAccess = useAppSelector((state) => state.app.loggedInUserDetails.manageFeedbackAccess);
  const viewAs = useAppSelector((state) => state.app.viewAsForAbovePropUser);

  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const isDashboard = location.pathname.split("/")[1] === "";

  const isAbovePropertyUser = isAbovePropertyUserFetched ? isAboveProperty : true;
  const isInitialOnboardingCompleted = getInitialWizardTaken();
  const initialWizard = useAppSelector((state) => state.onboardingResponse.initialWizard);
  const defaultFilter = useAppSelector((state) => state.filters?.savedFilter?.default);

  const isRoutesLoad = () => {
    if (!isImpersonated) {
      if (getIsCorpUser(decrypt("salt", localStorage.getItem("lobbyUserType")))) {
        return defaultFilter?.length;
      }
      return isInitialOnboardingCompleted || initialWizard;
    }
    return true;
  };

  return (
    <>
      <Snackbar />
      <ReleaseDialog />
      {isRoutesLoad() && (
        <Box
          component="main"
          className={isImpersonated ? "impersonate-main" : "non-impersonated-main"}
          marginLeft={{ md: "137px", xs: "16px" }}
          marginRight={{
            md: nestedTernaryCheckerCond1(isDashboard, !isConsultant && isCorp, "32px", "0", "32px"),
            xs: "16px"
          }}
          paddingY={{ xs: "16px", md: "unset" }}
          sx={{
            borderTop:
              location.pathname.split("/")[1] !== "library" && !isMobileScreen
                ? `1px solid ${tokens.Border.General.Tertiary}`
                : "none"
          }}
        >
          {isMobileScreen && <BackButton label="Back" />}
          <Routes>
            <Route element={<DialogsContextLayout />}>
              <Route path={routes.ProgressInfo} element={<Progress />} />
              {manageFeedbackAccess !== "Disabled" && <Route path={routes.Feedback} element={<Feedback />} />}
              <Route path={`${routes.EditFeedback}/:id`} element={<EditFeedback />} />

              {isCorp && (
                <>
                  <Route path={routes.IndividualProperty} element={<IndividualProperty isIndividualPropertyPage />} />
                  {/* <Route path={routes.InncodeUsers} element={<InncodeUsers />} /> */}
                  <Route path={routes.UserDashboard} element={<HotelUserDashboard />} />
                  {adminConsoleAccess && (
                    <>
                      <Route path={routes.AdminConsole} element={<AdminConsole />} />
                      <Route path={routes.EditPermission} element={<EditPermission />} />
                    </>
                  )}
                </>
              )}
              {isCorp && !isConsultant && (
                <>
                  <Route path={routes.MyLists} element={<MyLists />} />
                  <Route path={routes.Dashboard} element={<CorpDashboard label="My Dashboard" />} />
                </>
              )}

              {isConsultant && (
                <>
                  <Route path={routes.Dashboard} element={<MyLists />} />
                  <Route path={routes.CorporateDashboard} element={<CorpDashboard label="Corporate Dashboard" />} />
                  <Route path={routes.MyLists} element={<MyLists />} />
                </>
              )}

              {!isCorp && (
                <>
                  {isAbovePropertyUser && isAbovePropertyUserFetched && viewAs === abovePropUserView["AP"] ? (
                    <>
                      <Route
                        path={routes.IndividualProperty}
                        element={<IndividualProperty isIndividualPropertyPage />}
                      />
                      <Route path={routes.Dashboard} element={<AboveProperty />} />
                      <Route path={routes.UserDashboard} element={<HotelUserDashboard />} />
                    </>
                  ) : (
                    <Route path={routes.Dashboard} element={<HotelUserDashboard />} />
                  )}
                </>
              )}
              <Route path={routes.Library} element={<Library />} />
              <Route path={routes.MyPlans} element={<MyPlans />} />
              {uploadRecommendationAccess !== recommendationAccessTypes.disabled && (
                <Route path={routes.ManageSuggestions} element={<ManageSuggestions />} />
              )}
              <Route path={routes.UserView} element={<IndividualUser />} />

              <Route path={routes.FunctionalArea} element={<FunctionalArea />} />
              <Route path={routes.MyPlanDetails} element={<MyPlanDetails />}>
                <Route path={routes.sectionDetails} element={<MyPlanDetails />}>
                  <Route path={routes.CardDetails} element={<MyPlanDetails />} />
                </Route>
              </Route>
              <Route path={routes.LibraryHallSearch} element={<LibraryHallSearch />} />
              <Route path={routes.PlanDetails} element={<PlanDetails />}>
                <Route path={routes.sectionDetails} element={<PlanDetails />}>
                  <Route path={routes.CardDetails} element={<PlanDetails />} />
                </Route>
              </Route>
              <Route path={`${routes.PlanProgress}/:id`} element={<PlanProgress />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      )}
    </>
  );
};

export default BoostRoutes;
